import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import { Drawer } from 'src/design/molecules/Drawer';
import { Menu } from 'src/design/molecules/Menu';
import {
  IconArrowLeft,
  IconFileExport,
  IconMarkdown,
  IconMessagePlus,
  IconPrinter,
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  MAX_CONVERSATION_TITLE_LENGTH,
  useChat,
  useChatUi,
} from 'src/hooks/useChat';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { FireIcon } from 'src/design/atoms/FireIcon';
import classes from 'src/app/chat/chat.module.css';
import { ChatMemoryMenu } from 'src/app/chat/ChatMemoryMenu';
import { ChatPanel } from 'src/app/chat/ChatPanel';
import { ConversationHistory } from 'src/app/chat/ConversationHistory';
import { UNTITLED_CONVERSATION_TITLE } from 'src/app/chat/transcript';
import { useChatTranscriptExport } from 'src/app/chat/useChatTranscriptExport';

/** @registry Global slide-over AI assistant drawer (conversation list ↔ active chat panel); mount once at app root, opened/closed via useChatUi. */
export const ChatDrawer: React.FC = () => {
  const { drawerOpen, closeDrawer } = useChatUi();
  const {
    newConversation,
    conversations,
    conversationId,
    loadingConversations,
    selectConversation,
    deleteConversationSafely,
    renameConversationTitle,
    memoryOff,
  } = useChat();

  const activeTitle = conversations.find((c) => c.id === conversationId)?.title;
  const transcriptExport = useChatTranscriptExport();

  // Below the tablet breakpoint the slide-over would leave too little room for
  // the chat panel, so it goes full-screen instead.
  const isSmallViewport = useMediaQuery('(max-width: 48em)');

  const [view, setView] = useState<'list' | 'chat'>('list');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  // Escape/Enter both tear down the input, whose resulting blur would
  // otherwise fire a second (unwanted) commit — this suppresses that blur.
  const skipBlurRef = useRef(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (drawerOpen) setView('list');
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen || view === 'list') setEditingTitle(false);
  }, [drawerOpen, view]);

  const startTitleEdit = () => {
    setTitleDraft(activeTitle || '');
    setEditingTitle(true);
  };

  const cancelTitleEdit = () => {
    skipBlurRef.current = true;
    setEditingTitle(false);
  };

  const commitTitleEdit = (value: string) => {
    skipBlurRef.current = true;
    setEditingTitle(false);
    const trimmed = value.trim();
    if (!trimmed || !conversationId) return;
    renameConversationTitle(conversationId, trimmed);
  };

  useEffect(() => {
    if (!drawerOpen || view !== 'chat' || editingTitle) return;
    // Drawer content mounts immediately, but the open transition can steal
    // focus; defer briefly so the textarea keeps it. Suppressed while the title
    // is being edited so the deferred focus does not yank focus off the input.
    const id = setTimeout(() => composerRef.current?.focus(), 150);
    return () => clearTimeout(id);
  }, [drawerOpen, view, editingTitle]);

  const handleSelect = (id: string) => {
    setView('chat');
    void selectConversation(id);
  };

  const handleNewConversation = () => {
    newConversation();
    setView('chat');
  };

  const handleNewConversationWithoutMemory = () => {
    newConversation({ memoryOff: true });
    setView('chat');
  };

  const memoryMenu = (
    <ChatMemoryMenu
      onNewChatWithoutMemory={handleNewConversationWithoutMemory}
      onManageMemory={closeDrawer}
    />
  );

  return (
    <Drawer
      opened={drawerOpen}
      onClose={closeDrawer}
      position="right"
      size={isSmallViewport ? '100%' : 'xl'}
      title={
        view === 'chat' ? (
          <Stack gap={4}>
            {editingTitle ? (
              <TextInput
                size="xs"
                aria-label="Conversation title"
                // The drawer closes on Escape from a window capture-phase listener, which runs before
                // onKeyDown; the attribute is the only way to keep Escape from also closing the drawer.
                data-mantine-stop-propagation="true"
                maxLength={MAX_CONVERSATION_TITLE_LENGTH}
                autoFocus
                value={titleDraft}
                onChange={(event) => setTitleDraft(event.currentTarget.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitTitleEdit(event.currentTarget.value);
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelTitleEdit();
                  }
                }}
                onBlur={(event) => {
                  if (skipBlurRef.current) {
                    skipBlurRef.current = false;
                    return;
                  }
                  commitTitleEdit(event.currentTarget.value);
                }}
              />
            ) : (
              <Group gap={4} wrap="nowrap">
                <Text fw={600} c="bright">
                  {activeTitle || UNTITLED_CONVERSATION_TITLE}
                </Text>
                {memoryOff && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="gray"
                    style={{ flexShrink: 0 }}
                  >
                    Memory off
                  </Badge>
                )}
                {conversationId && (
                  <EditActionIcon
                    size="sm"
                    variant="subtle"
                    aria-label="Rename conversation"
                    onClick={startTitleEdit}
                  />
                )}
              </Group>
            )}
            <Group gap={4}>
              <BasicActionIcon
                variant="subtle"
                label="Back to conversations"
                onClick={() => setView('list')}
              >
                <IconArrowLeft size={18} />
              </BasicActionIcon>
              <BasicActionIcon
                variant="subtle"
                label="New conversation"
                onClick={handleNewConversation}
              >
                <IconMessagePlus size={18} />
              </BasicActionIcon>
              <Menu position="bottom-start" withinPortal>
                <Menu.Target>
                  <BasicActionIcon
                    variant="subtle"
                    label="Export conversation"
                    disabled={!transcriptExport.canExport}
                  >
                    <IconFileExport size={18} />
                  </BasicActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconMarkdown size={16} />}
                    onClick={transcriptExport.downloadMarkdown}
                  >
                    Markdown (.md)
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconPrinter size={16} />}
                    onClick={transcriptExport.print}
                  >
                    Print / Save as PDF
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              {memoryMenu}
            </Group>
          </Stack>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <FireIcon size={18} />
            <Text fw={600} c="bright">
              F.I.R.E. Chats
            </Text>
            {memoryMenu}
          </Group>
        )
      }
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          // dvh tracks the visual viewport, so the drawer shrinks with the
          // on-screen keyboard on mobile instead of being clipped below it.
          ...(isSmallViewport ? { height: '100dvh' } : {}),
        },
        body: { flex: 1, minHeight: 0, padding: 0 },
      }}
    >
      <Box className={classes.slideWrapper}>
        <Box
          className={classes.slideTrack}
          style={{
            transform: `translateX(${view === 'chat' ? '-100%' : '0'})`,
          }}
        >
          <Box
            className={classes.slidePanel}
            aria-hidden={view !== 'list'}
            inert={view !== 'list' ? '' : undefined}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Box
              p="sm"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
            >
              <CreateButton
                fullWidth
                leftSection={<IconMessagePlus size={16} />}
                onClick={handleNewConversation}
              >
                New conversation
              </CreateButton>
            </Box>
            <Box style={{ flex: 1, minHeight: 0 }}>
              <ConversationHistory
                conversations={conversations}
                activeConversationId={conversationId}
                loading={loadingConversations}
                onSelect={handleSelect}
                onDelete={deleteConversationSafely}
              />
            </Box>
          </Box>
          <Box
            className={classes.slidePanel}
            aria-hidden={view !== 'chat'}
            inert={view !== 'chat' ? '' : undefined}
          >
            <ChatPanel
              composerRef={composerRef}
              onRefClick={isSmallViewport ? closeDrawer : undefined}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
